import {
  isGuestWorkspaceRole,
  useCurrentAccount,
  useRoles,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import { LoadingIndicator } from '@gripp/shared-ui';
import type { ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';

/**
 * Wraps every asset sub-route that is off a Guest's allowlist (plan 0030
 * Phase 5: the header plus their own conversation, nothing else).
 * `ProtectedRoute`'s `requiredPermission` check runs against the caller's
 * AMBIENT role -- whatever workspace they are currently switched into, e.g.
 * My History or their own personal workspace -- not the asset's own
 * workspace, so it cannot tell a Guest of THIS asset apart from a member
 * elsewhere holding the same permission name. This re-checks membership
 * scoped to the asset itself.
 *
 * A redirect can't be undone from the user's side, so it only fires on a
 * RESOLVED, positive guest determination -- unresolved account/roles/
 * workspace data holds on a loading state instead, it never navigates.
 */
export const AssetSubRouteGuestGate = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { assetId } = useParams();
  const { workspaceAsset, isLoading } = useWorkspaceAsset(assetId ?? '');
  const { account } = useCurrentAccount();
  const { roles } = useRoles();
  const assetWorkspaceId = workspaceAsset?.workspace?.id;
  const isResolved = !isLoading && !!assetWorkspaceId;

  if (!isResolved) {
    return <LoadingIndicator flexContainer />;
  }

  if (isGuestWorkspaceRole(assetWorkspaceId as string, account?.workspaceRoles, roles)) {
    return <Navigate to="/forbidden" replace />;
  }

  return children;
};
